import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, ArrowRight, SlidersHorizontal, Search, X as ClearIcon } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { MARKETPLACE } from "@/constants/testIds";
import { toast } from "sonner";
import { usePageTitle } from "@/hooks/usePageTitle";
import ProductImage from "@/components/ProductImage";

const SORT_OPTIONS = [
  { value: "default", label: "Featured" },
  { value: "price_asc", label: "Price: low → high" },
  { value: "price_desc", label: "Price: high → low" },
  { value: "name_asc", label: "A → Z" },
];

function applySort(products, sort) {
  const arr = [...products];
  if (sort === "price_asc") return arr.sort((a, b) => a.price - b.price);
  if (sort === "price_desc") return arr.sort((a, b) => b.price - a.price);
  if (sort === "name_asc") return arr.sort((a, b) => a.name.localeCompare(b.name));
  return arr;
}

function SkeletonCard() {
  return (
    <div className="glass rounded-2xl overflow-hidden flex flex-col animate-pulse">
      <div className="aspect-[4/3] bg-white/5" />
      <div className="p-5 flex-1 flex flex-col gap-3">
        <div className="h-2.5 bg-white/5 rounded w-1/3" />
        <div className="h-3.5 bg-white/5 rounded w-3/4" />
        <div className="h-2.5 bg-white/5 rounded w-full" />
        <div className="h-2.5 bg-white/5 rounded w-2/3" />
        <div className="mt-auto flex items-center justify-between">
          <div className="h-5 bg-white/5 rounded w-1/4" />
          <div className="w-9 h-9 rounded-full bg-white/5" />
        </div>
      </div>
    </div>
  );
}

export default function Marketplace() {
  usePageTitle(
    "Marketplace",
    "Buy WeAutomate hardware direct: smart lights, switches, gateways, sensors, and locks. Engineered in India with tiered pricing for retailers, distributors, and franchise partners."
  );
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState(["all"]);
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("default");
  const [query, setQuery] = useState("");
  const { add } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    setLoading(true);
    api.get("/products").then(({ data }) => {
      setProducts(data);
      const cats = ["all", ...Array.from(new Set(data.map((d) => d.category)))];
      setCategories(cats);
    }).finally(() => setLoading(false));
  }, []);

  // Search + category + sort pipeline. Memoized so typing does not
  // re-run the sort on every keystroke unless the source data or the
  // query itself actually changed.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let base = category === "all" ? products : products.filter((p) => p.category === category);
    if (q) {
      base = base.filter((p) => {
        const hay = `${p.name} ${p.short_desc || ""} ${p.description || ""} ${p.sku || ""} ${p.category || ""}`.toLowerCase();
        return hay.includes(q);
      });
    }
    return applySort(base, sort);
  }, [products, category, sort, query]);

  const onAdd = (p) => {
    if (p.visibility === "coming_soon") { toast.info(`${p.name} is coming soon`); return; }
    add(p, 1);
    toast.success(`${p.name} added to cart`);
  };

  return (
    <div className="py-20">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Marketplace · WeAutomate-branded</div>
        <h1 className="font-display text-4xl lg:text-6xl mt-3 tracking-tighter max-w-3xl">
          The <span className="gradient-text">Smart Home line</span>.
        </h1>
        <p className="mt-4 text-white/60 max-w-2xl">
          Designed, manufactured and supported in India. Tier pricing applied automatically based on your account.
          {user && <span className="ml-2 text-xs font-mono uppercase tracking-widest text-white/40">Your tier: {products[0]?.tier || "retail"}</span>}
        </p>

        {/* Search bar */}
        <div className="mt-10 relative max-w-md">
          <Search className="w-4 h-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products, SKUs, categories..."
            aria-label="Search products"
            className="w-full bg-white/5 border border-white/10 rounded-full pl-11 pr-11 py-2.5 text-sm outline-none focus:border-[#9D4CDD]"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white p-1"
            >
              <ClearIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Filters + sort row */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <div data-testid={MARKETPLACE.categoryFilter} className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button key={c} onClick={() => setCategory(c)}
                      data-testid={`marketplace-cat-${c}`}
                      className={`text-xs uppercase tracking-widest font-mono px-4 py-2 rounded-full border transition-colors ${
                        category === c ? "bg-accent border-transparent text-white" : "border-white/15 text-white/60 hover:text-white hover:border-white/30"
                      }`}>
                {c === "all" ? "All" : c}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 text-xs text-white/60">
            <SlidersHorizontal className="w-3.5 h-3.5 text-accent" />
            <select value={sort} onChange={(e) => setSort(e.target.value)}
                    className="bg-transparent border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white/70 hover:border-white/25 focus:outline-none cursor-pointer">
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} className="bg-[#11131A]">{o.label}</option>
              ))}
            </select>
          </div>
        </div>
        {query && !loading && (
          <div className="mt-4 text-xs text-white/40 font-mono uppercase tracking-widest">
            {filtered.length} result{filtered.length === 1 ? "" : "s"} for "{query}"
          </div>
        )}

        {/* Grid */}
        <div data-testid={MARKETPLACE.grid}
             className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {loading
            ? Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)
            : filtered.length === 0
              ? (
                <div className="col-span-full py-24 text-center">
                  <div className="text-white/30 text-sm">No products in this category yet.</div>
                  <button onClick={() => setCategory("all")} className="mt-4 text-xs text-accent hover:text-white/80">
                    View all products →
                  </button>
                </div>
              )
              : filtered.map((p) => (
                <div key={p.id} data-testid={MARKETPLACE.productCard(p.slug)}
                     className="group glass rounded-2xl overflow-hidden flex flex-col hover:border-white/25 transition-all">
                  <Link to={`/product/${p.slug}`} className="aspect-[4/3] overflow-hidden block bg-white/5">
                    <ProductImage src={p.images?.[0]} alt={p.name}
                                  className="group-hover:scale-105 transition-transform duration-700" />
                  </Link>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono">{p.category}</div>
                    <Link to={`/product/${p.slug}`} className="mt-1.5 text-sm font-medium leading-tight hover:text-accent transition-colors">
                      {p.name}
                    </Link>
                    <p className="text-xs text-white/50 mt-2 flex-1 leading-relaxed">{p.short_desc}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <div>
                        <div className="font-display text-lg text-accent">₹{p.price.toLocaleString("en-IN")}</div>
                        {p.tier !== "retail" && (
                          <div className="text-[9px] uppercase tracking-widest text-accent/60 font-mono">{p.tier} price</div>
                        )}
                      </div>
                      <button data-testid={MARKETPLACE.addToCart(p.slug)} onClick={() => onAdd(p)}
                              className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:bg-accent hover:border-transparent transition-all">
                        {p.visibility === "coming_soon"
                          ? <ArrowRight className="w-4 h-4" />
                          : <ShoppingCart className="w-4 h-4" />}
                      </button>
                    </div>
                    {p.visibility === "coming_soon" && (
                      <div className="mt-3 text-[10px] uppercase tracking-widest text-amber-400/80 font-mono">Coming soon</div>
                    )}
                  </div>
                </div>
              ))
          }
        </div>
      </div>
    </div>
  );
}
